import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { ContactDto, ContactType } from '@/shared/lib/sj-orm/models/contact.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import { DebitAccountDetails, DebitAccountGeneral } from '../../sj-orm/models/assets/categories';
import { DebitType } from '../../sj-orm/models/assets/enums';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type DebitAccount = DebitAccountFormGeneralType &
  DebitAccountDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const DebitList = [
  {
    label: 'forms_assets:debitAccount.enums.type.currentAccount',
    key: DebitType.currentAccount,
  },
  {
    label: 'forms_assets:debitAccount.enums.type.deposit',
    key: DebitType.deposit,
  },
];

type DebitAccountHelpGeneral = {
  nickName: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  type: QuestionType.SELECT_FROM_LIST;
  purchaseValue: QuestionType.CURRENCY_CALCULATOR;
  bank: QuestionType.PICK_ARRAY_OF_DTO; //fix
};

type DebitAccountHelpDetails = {
  cif?: QuestionType.TEXT;
  iban?: QuestionType.TEXT;
  dateOfOpening?: QuestionType.DATE_TIME;
  fixedTermAccount?: QuestionType.CHECKBOX;
  activeUntil?: QuestionType.DATE_TIME;
  attachments?: QuestionType.FILE_UPLOAD;
};

type DebitAccountFormGeneralType = TDtoWrapper<DebitAccountGeneral, DebitAccountHelpGeneral>;
type DebitAccountDetailsType = TDtoWrapper<DebitAccountDetails, DebitAccountHelpDetails>;

const DebitAccountFormGeneral: DebitAccountFormGeneralType = {
  nickName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:debitAccount.inputs.type.label',
    value: DebitType.currentAccount,
    items: DebitList,
    required: true,
  },
  purchaseValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:debitAccount.inputs.currentValue.label',
    tooltip: 'forms_assets:debitAccount.inputs.currentValue.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  bank: {
    type: QuestionType.PICK_ONE_OF_DTOS,
    label: 'forms_assets:debitAccount.inputs.bank.label',
    tooltip: 'forms_assets:debitAccount.inputs.bank.tooltip',
    colName: CollectionName.CONTACTS,
    required: true,
    filter: (contact) => (contact as unknown as ContactDto).type === ContactType.BANK,
    value: [],
    dtoType: [ContactType.BANK],
  },
};

const DebitAccountFormDetails: DebitAccountDetailsType = {
  cif: {
    type: QuestionType.TEXT,
    label: 'forms_assets:debitAccount.inputs.cif.label',
    order: 1,
  },
  iban: {
    type: QuestionType.TEXT,
    label: 'forms_assets:debitAccount.inputs.iban.label',
    order: 2,
  },
  dateOfOpening: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:debitAccount.inputs.dateOfOpening.label',
    order: 3,
  },
  activeUntil: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:debitAccount.inputs.activeUntil.label',
    disableIf: {
      fixedTermAccount: {
        value: true,
        shouldClean: true,
      },
    },
    order: 4,
  },
  fixedTermAccount: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:debitAccount.inputs.fixedTermAccount.label',
    order: 5,
  },
  attachments: baseAssetFields.attachments,
  // insuranceAgreement: baseAassetFields.insuranceAgreement,
};

const DebitAccountForm = [
  DebitAccountFormGeneral,
  DebitAccountFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'taxAgentWithBroker',
    'taxAgentWithBrokerOther',
    'publicFundTaxAgent',
    'publicTaxAgentOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { DebitAccountForm };
